/**
 * Admin API Keys View
 * For admins to manage all users' API keys
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Key,
  Search,
  Plus,
  Filter,
  Download,
  Trash2,
  MoreVertical,
  Shield,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import { useDashboardStore } from "@/store";
import { cn } from "@/lib/utils";
import { apiKeysService } from "@/services/apiKeysService";

interface UserApiKey {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  name: string;
  keyPreview: string;
  scopes: string[];
  createdAt: string;
  lastUsed?: string;
  status: "active" | "revoked" | "expired";
  rateLimit: number;
  callsToday: number;
  totalCalls: number;
  monthlyCost: number;
}

export function AdminApiKeysView() {
  const { addLogEntry } = useDashboardStore();
  const [keys, setKeys] = useState<UserApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "revoked" | "expired">("all");
  const [sortField, setSortField] = useState<"name" | "user" | "created" | "usage">("created");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockKeys: UserApiKey[] = [
        {
          id: "1",
          userId: "u1",
          userEmail: "john@example.com",
          userName: "John Doe",
          name: "Production Key",
          keyPreview: "sk-proj-bnk_abc1...234yz",
          scopes: ["read", "write", "agi"],
          createdAt: "2025-01-15T10:30:00Z",
          lastUsed: "2025-12-28T15:45:00Z",
          status: "active",
          rateLimit: 10000,
          callsToday: 1245,
          totalCalls: 124580,
          monthlyCost: 125.50,
        },
        {
          id: "2",
          userId: "u2",
          userEmail: "jane@example.com",
          userName: "Jane Smith",
          name: "Development Key",
          keyPreview: "sk-proj-bnk_xyz7...012u",
          scopes: ["read", "agi"],
          createdAt: "2025-06-01T09:15:00Z",
          lastUsed: "2025-12-29T08:20:00Z",
          status: "active",
          rateLimit: 5000,
          callsToday: 892,
          totalCalls: 45678,
          monthlyCost: 89.20,
        },
        {
          id: "3",
          userId: "u1",
          userEmail: "john@example.com",
          userName: "John Doe",
          name: "Testing Key",
          keyPreview: "sk-proj-bnk_lmn4...45jkl",
          scopes: ["read"],
          createdAt: "2025-11-20T14:00:00Z",
          status: "revoked",
          rateLimit: 1000,
          callsToday: 0,
          totalCalls: 2341,
          monthlyCost: 5.00,
        },
        {
          id: "4",
          userId: "u3",
          userEmail: "bob@example.com",
          userName: "Bob Johnson",
          name: "Client Key",
          keyPreview: "sk-proj-bnk_def0...678vwx",
          scopes: ["read", "write", "delete", "agi"],
          createdAt: "2025-08-10T11:00:00Z",
          lastUsed: "2025-12-27T22:15:00Z",
          status: "active",
          rateLimit: 50000,
          callsToday: 8456,
          totalCalls: 567890,
          monthlyCost: 567.89,
        },
      ];
      
      setKeys(mockKeys);
    } catch (error) {
      console.error("Failed to load API keys:", error);
      addLogEntry({
        category: "error",
        action: "Load API Keys",
        details: "Failed to load user API keys",
        severity: "error",
        source: "admin-api-keys",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredAndSortedKeys = keys
    .filter(key => {
      const matchesSearch = 
        key.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        key.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        key.userEmail.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === "all" || key.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "user":
          comparison = a.userName.localeCompare(b.userName);
          break;
        case "created":
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case "usage":
          comparison = a.totalCalls - b.totalCalls;
          break;
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });

  const revokeKey = async (id: string) => {
    try {
      setKeys(keys.map(k => 
        k.id === id ? { ...k, status: "revoked" as const } : k
      ));
      
      addLogEntry({
        category: "security",
        action: "API Key Revoked (Admin)",
        details: `Revoked API key: ${id}`,
        severity: "warning",
        source: "admin-api-keys",
      });
    } catch (error) {
      console.error("Failed to revoke key:", error);
    }
  };

  const exportKeys = () => {
    const csv = [
      ["Name", "User", "Email", "Status", "Created", "Total Calls", "Monthly Cost"].join(","),
      ...filteredAndSortedKeys.map(k => [
        k.name,
        k.userName,
        k.userEmail,
        k.status,
        new Date(k.createdAt).toLocaleDateString(),
        k.totalCalls.toLocaleString(),
        `$${k.monthlyCost.toFixed(2)}`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "api-keys.csv";
    a.click();
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const totalCost = keys.reduce((sum, k) => sum + k.monthlyCost, 0);
  const totalCalls = keys.reduce((sum, k) => sum + k.totalCalls, 0);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Key className="mx-auto h-12 w-12 animate-pulse text-[hsl(var(--muted-foreground))]" />
          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">Loading API keys...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">API Keys Management</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Manage all users' API keys</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportKeys}
            className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-4 py-2 text-sm font-medium hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
              <Key className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{keys.length}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Total Keys</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
              <Shield className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{keys.filter(k => k.status === "active").length}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Active Keys</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10">
              <Filter className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalCalls.toLocaleString()}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Total Calls</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">${totalCost.toFixed(2)}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Monthly Revenue</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center justify-between rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
        <div className="flex items-center gap-3 flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          <input
            type="text"
            placeholder="Search keys, users, or emails..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent text-sm focus:outline-none"
          />
        </div>
        
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="revoked">Revoked</option>
            <option value="expired">Expired</option>
          </select>

          <select
            value={`${sortField}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split("-");
              setSortField(field as any);
              setSortOrder(order as any);
            }}
            className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
          >
            <option value="created-desc">Newest First</option>
            <option value="created-asc">Oldest First</option>
            <option value="usage-desc">Most Used</option>
            <option value="usage-asc">Least Used</option>
            <option value="name-asc">Name A-Z</option>
            <option value="name-desc">Name Z-A</option>
          </select>
        </div>
      </div>

      {/* Keys Table */}
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted)/.3)]">
              <tr className="text-left text-xs uppercase text-[hsl(var(--muted-foreground))]">
                <th className="px-6 py-3 font-medium">Key Name</th>
                <th className="px-6 py-3 font-medium">User</th>
                <th className="px-6 py-3 font-medium">Scopes</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Created</th>
                <th className="px-6 py-3 font-medium text-right">Usage</th>
                <th className="px-6 py-3 font-medium text-right">Cost</th>
                <th className="px-6 py-3 font-medium text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {filteredAndSortedKeys.map((key, index) => (
                  <motion.tr
                    key={key.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ delay: index * 0.02 }}
                    className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted)/.3)] transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium">{key.name}</p>
                        <code className="text-xs text-[hsl(var(--muted-foreground))]">{key.keyPreview}</code>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium">{key.userName}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">{key.userEmail}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {key.scopes.map((scope) => (
                          <span
                            key={scope}
                            className="rounded bg-[hsl(var(--muted))] px-1.5 py-0.5 text-xs font-medium capitalize"
                          >
                            {scope}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {key.status === "active" ? (
                        <span className="flex items-center gap-1.5 rounded-full bg-green-500/10 px-2.5 py-1 text-xs font-medium text-green-500">
                          <CheckCircle className="h-3 w-3" />
                          Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 rounded-full bg-red-500/10 px-2.5 py-1 text-xs font-medium text-red-500">
                          <AlertTriangle className="h-3 w-3" />
                          Revoked
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {formatDate(key.createdAt)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm">
                      <div>
                        <p className="font-medium">{key.totalCalls.toLocaleString()}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">{key.callsToday} today</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      ${key.monthlyCost.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => revokeKey(key.id)}
                          disabled={key.status !== "active"}
                          className={cn(
                            "rounded p-1.5 hover:bg-red-500/20 transition-colors cursor-pointer",
                            key.status === "active" ? "text-red-500" : "text-[hsl(var(--muted-foreground))] cursor-not-allowed"
                          )}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {filteredAndSortedKeys.length === 0 && (
          <div className="p-12 text-center text-[hsl(var(--muted-foreground))]">
            <Key className="mx-auto h-12 w-12 mb-4 opacity-50" />
            <p>No API keys found matching your filters</p>
          </div>
        )}
      </div>
    </div>
  );
}
